import * as Preact from "preact";
import * as _ from "underscore";

import * as accountModal from "accountModal";
import loadHandwritingFont from "loadHandwritingFont";
import models from "models/models";
import { cn } from "shared/utils";
import * as themeColor from "themeColor";
import * as browserTTSVoices from "../browserTTSVoices";

const ranges = {
  fontSize: {
    min: 0,
    max: 8,
    initial: 5,
  },
  width: {
    min: 1,
    max: 4,
    initial: 3,
  },
  lineHeight: {
    min: 0,
    max: 4,
    initial: 2,
  },
};

const getNumber = (name) => {
  const body = $("body");
  for (let number = 0; number < 9; number++) {
    if (body.hasClass(name + number)) {
      return number;
    }
  }
  return ranges[name].initial;
};

interface Props {
  transliteration?: string;
  showReaderReviewOption: boolean;
  ttsVoices: { voiceURI: string; displayName: string; lang: string }[];
  restartTTSIfPlayingOrPlayDemo: () => void;
  audioFileMode?: boolean;
}

/** Map of font name to the value of the `font-family` CSS attribute */
const fontToStyle = {
  Helvetica: "Helvetica, Arial, sans-serif",
  "Open Sans": "'Open Sans', sans-serif",
  "Times New Roman": "'Times New Roman', serif",
  Jost: "Jost, serif",
};

class FormattingOptions extends Preact.Component<Props> {
  /** A JQuery element corresponding to the HTML body. */
  body: JQuery;

  capitalizeFirstLetter(input) {
    return input.charAt(0).toUpperCase() + input.slice(1);
  }

  /**
   * Apply the settings to the page.
   *
   * It's hacky to do all this in this view module. Consider moving elsewhere.
   */
  applySettingsToPage() {
    // Font
    this.body
      .find("#bookContent")
      .css(
        "font-family",
        fontToStyle[models.user.get("readerFont")] ??
          models.user.get("readerFont")
      );

    // Translation style
    const translationStyle = models.user.get("readerTranslationStyle");
    this.body.removeClass("glossStyleNew");
    this.body.removeClass("glossStyle");

    if (translationStyle === "gloss") {
      loadHandwritingFont();
      this.body.addClass("glossStyle");
    } else if (translationStyle === "glossNew") {
      loadHandwritingFont();
      this.body.addClass("glossStyleNew");
    }

    // Translations
    if (models.user.get("readerTranslations") === "off") {
      this.body.addClass("translationsOff");
    } else {
      this.body.removeClass("translationsOff");
    }

    // Alignment
    this.setBodyClass(models.user.get("readerAlign"), [
      "alignLeft",
      "alignJustify",
    ]);

    // Update color scheme
    const colorScheme = localStorage.getItem("reader.color") ?? "colorSepia";
    this.setBodyClass(colorScheme, ["colorLight", "colorSepia", "colorDark"]);
    themeColor.update();
  }

  setBodyClass(toAdd, possibleValues) {
    for (const className of _.without(possibleValues, toAdd)) {
      this.body.removeClass(className);
    }
    this.body.addClass(toAdd);
  }

  setNumber(name, number) {
    this.body.removeClass(name + getNumber(name));
    this.body.addClass(name + number);
    localStorage.setItem("reader." + name, number);
  }

  increaseNumber(name) {
    let value = getNumber(name);
    if (value < ranges[name].max) {
      value++;
      this.setNumber(name, value);
    }
  }

  decreaseNumber(name) {
    let value = getNumber(name);
    if (value > ranges[name].min) {
      value--;
      this.setNumber(name, value);
    }
  }

  constructor(props: Props) {
    super(props);

    this.body = $("body");

    this.setNumber(
      "fontSize",
      localStorage.getItem("reader.fontSize") || ranges.fontSize.initial
    );
    this.setNumber(
      "lineHeight",
      localStorage.getItem("reader.lineHeight") || ranges.lineHeight.initial
    );
    this.setNumber(
      "width",
      localStorage.getItem("reader.width") || ranges.width.initial
    );

    models.user.on("change", this.onChange);

    this.applySettingsToPage();

    browserTTSVoices.onChange(this.handleChangeVoice);
  }

  handleChangeVoice = () => {
    this.forceUpdate();
  };

  componentWillUnmount() {
    this.body.removeClass("alignLeft");
    this.body.removeClass("alignJustify");

    this.body.removeClass("glossStyle");
    this.body.removeClass("glossStyleNew");
    models.user.off("change", this.onChange);
    browserTTSVoices.offChange(this.handleChangeVoice);
  }

  onChange = () => {
    this.forceUpdate();
    this.applySettingsToPage();
  };

  colorScheme = () => {
    return localStorage.getItem("reader.color") || "colorSepia";
  };

  ttsRate = () => {
    return browserTTSVoices.getRate(models.bookModel.get("language"));
  };

  render() {
    const { transliteration, showReaderReviewOption, ttsVoices } = this.props;

    const readAloudVoiceURI = browserTTSVoices.getVoice(
      models.bookModel.get("language")
    );

    return (
      <div id="formattingOptions">
        <h3>Change Size</h3>
        <p>
          <span
            class="clickable"
            onClick={() => this.decreaseNumber("fontSize")}
          >
            - Font
          </span>
          <span
            class="clickable"
            onClick={() => this.increaseNumber("fontSize")}
          >
            + Font
          </span>
        </p>
        <p>
          <span
            class="clickable"
            onClick={() => this.decreaseNumber("lineHeight")}
          >
            - Line Height
          </span>
          <span
            class="clickable"
            onClick={() => this.increaseNumber("lineHeight")}
          >
            + Line Height
          </span>
        </p>
        <p class="noMobile">
          <span class="clickable" onClick={() => this.decreaseNumber("width")}>
            <i class="icon-arrow-right"></i> Column{" "}
            <i class="icon-arrow-left"></i>
          </span>
          <span class="clickable" onClick={() => this.increaseNumber("width")}>
            <i class="icon-arrow-left"></i> Column{" "}
            <i class="icon-arrow-right"></i>
          </span>
        </p>

        <h3>Change Appearance</h3>
        <p>
          {["Helvetica", "Open Sans", "Times New Roman", "Jost"].map((font) => (
            <span
              class={cn({
                clickable: true,
                fontOption: true,
                selected: models.user.get("readerFont") === font,
              })}
              style={fontToStyle[font]}
              onClick={() =>
                models.user.save({ readerFont: font }, { patch: true })
              }
            >
              {font}
            </span>
          ))}
        </p>
        <p>
          {[
            { displayName: "E-Ink", color: "Light" },
            { displayName: "Light", color: "Sepia" },
            { displayName: "Dark", color: "Dark" },
          ].map(({ displayName, color }) => (
            <span
              class={cn({
                clickable: true,
                selected: this.colorScheme() === `color${color}`,
              })}
              onClick={() => {
                localStorage.setItem("reader.color", `color${color}`);
                this.onChange();
              }}
            >
              {displayName}
            </span>
          ))}
        </p>
        <UserFieldValueSelector
          fieldName="readerAlign"
          possibleValues={[
            {
              displayName: (
                <>
                  <i class="icon-align-left"></i> &nbsp; Align Left
                </>
              ),
              value: "alignLeft",
            },
            {
              displayName: (
                <>
                  <i class="icon-align-justify"></i> &nbsp; Justify
                </>
              ),
              value: "alignJustify",
            },
          ]}
        />
        <UserFieldValueSelector
          fieldName="readerSentenceBreaks"
          possibleValues={[
            {
              displayName: <>No Sentence Breaks</>,
              value: "off",
            },
            {
              displayName: <>Sentence Breaks</>,
              value: "on",
            },
          ]}
        />

        <h3>Translation Behavior</h3>
        <UserFieldValueSelector
          fieldName="readerTranslations"
          possibleValues={[
            {
              displayName: <>Translations On</>,
              value: "on",
            },
            {
              displayName: (
                <>
                  <span class="noMobile">Translations </span>Off{" "}
                  <small>(monolingual)</small>
                </>
              ),
              value: "off",
            },
          ]}
          style="fitContent"
        />
        {transliteration && (
          <p>
            <span
              class={cn({
                clickable: true,
                selected: models.user.hasFeature("showTransliterations"),
              })}
              style="width: fit-content"
              onClick={() =>
                models.user.setFeature("showTransliterations", true)
              }
            >
              Show {transliteration}
            </span>
            <span
              class={cn({
                clickable: true,
                selected: !models.user.hasFeature("showTransliterations"),
              })}
              style="width: fit-content"
              onClick={() =>
                models.user.setFeature("showTransliterations", false)
              }
            >
              Don't Show {transliteration}
            </span>
          </p>
        )}

        <p>
          <span
            class={cn({
              clickable: true,
              selected: !models.user.hasFeature("contextTranslations"),
            })}
            onClick={() => models.user.setFeature("contextTranslations", false)}
          >
            Basic Translations
          </span>
          <span
            class={cn({
              clickable: true,
              selected: models.user.hasFeature("contextTranslations"),
            })}
            onClick={(event) => {
              if (!models.user.hasPremiumAccess()) {
                accountModal.show({
                  model: models.user,
                  title: "contextTranslations",
                  messageHtml:
                    "<h2>Upgrade to enable context aware translations...</h2>",
                  showTitle: false,
                });
                event.preventDefault();
                return;
              }

              models.user.setFeature("contextTranslations", true);
            }}
          >
            Context Aware <small>(better)</small>
          </span>
        </p>
        <UserFieldValueSelector
          fieldName="readerTranslationStyle"
          possibleValues={[
            {
              displayName: <>Highlight</>,
              value: "glossNew",
            },
            {
              displayName: <>Underline</>,
              value: "gloss",
            },
            {
              displayName: <>Replace</>,
              value: "replace",
            },
          ]}
        />
        <UserFieldValueSelector
          fieldName="readerMergePhrases"
          possibleValues={[
            {
              displayName: <>Don't Merge</>,
              value: "off",
              title:
                "Adjacent words will be translated separately. Drag across words manually to translate phrases.",
            },
            {
              displayName: <>Merge Phrases</>,
              value: "on",
              title:
                "Selecting two adjacent words will combine them into a phrase.",
            },
          ]}
        />

        <h3>On Deselect Word/Phrase</h3>
        <UserFieldValueSelector
          fieldName="readerDeselectBehavior"
          possibleValues={[
            {
              displayName: <>Delete Flashcard</>,
              value: "delete",
            },
            {
              displayName: <>Keep Flashcard</>,
              value: "keep",
            },
          ]}
        />

        {showReaderReviewOption && (
          <>
            <h3>Review Summary Buttons</h3>
            <UserFieldValueSelector
              fieldName="readerReview"
              possibleValues={[
                {
                  displayName: <>Show</>,
                  value: "show",
                },
                {
                  displayName: <>Hide</>,
                  value: "hide",
                },
              ]}
            />
          </>
        )}

        <div class="autoHighlightOptions">
          <h3>Auto-Highlight Words</h3>
          <UserFieldValueSelector
            fieldName="readerWordHighlights"
            possibleValues={[
              {
                displayName: <>None</>,
                value: "off",
              },
              {
                displayName: <>Ready To Learn</>,
                value: "readyToLearn",
              },
              {
                displayName: <>All Your Words</>,
                value: "on",
              },
            ]}
            style="fitContent"
          />
        </div>

        <h3 class="streakOptions">Show Streak In Topbar</h3>
        <UserFieldValueSelector
          fieldName="readerStreakShow"
          possibleValues={[
            {
              displayName: <>Always Show</>,
              value: "always",
            },
            {
              displayName: <>Show Until Complete</>,
              value: "untilComplete",
            },
            {
              displayName: <>Never Show</>,
              value: "never",
            },
          ]}
          style="column"
        />

        {ttsVoices.length > 0 && !this.props.audioFileMode ? (
          <div class="readAloudVoiceSetting">
            <h3 class="readAloudVoice">Read Aloud Voice</h3>
            <small>
              Exact voices available depends on your device and browser. Some
              voices might not support highlighting of every word spoken.
            </small>
            <ul class="readAloudVoice">
              {ttsVoices.map((voice) => (
                <li>
                  <span
                    class={cn({
                      clickable: true,
                      readAloudVoiceOption: true,
                      selected: voice.voiceURI === readAloudVoiceURI,
                    })}
                    onClick={() => {
                      browserTTSVoices.setVoice(
                        models.bookModel.get("language"),
                        voice.voiceURI
                      );
                      this.onChange();
                      this.props.restartTTSIfPlayingOrPlayDemo?.();
                    }}
                  >
                    {voice.displayName}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {ttsVoices.length > 0 || this.props.audioFileMode ? (
          <div class="readAloudRateSetting">
            <h3 class="readAloudRateHeading">Read Aloud Speed</h3>
            <p class="readAloudRate">
              {[0.6, 0.8, 1, 1.2, 1.5, 2].map((rate) => (
                <span
                  class={cn({
                    clickable: true,
                    readAloudRateOption: true,
                    selected: rate.toString() === this.ttsRate(),
                  })}
                  data-value={rate}
                  onClick={() => {
                    browserTTSVoices.setRate(
                      models.bookModel.get("language"),
                      rate.toString()
                    );
                    this.onChange();
                    this.props.restartTTSIfPlayingOrPlayDemo?.();
                  }}
                >
                  {rate}X
                </span>
              ))}
            </p>
          </div>
        ) : null}

        <p style="margin-top: 24px;">
          <small>
            <a
              target="_blank"
              href="https://forum.readlang.com/t/guide-to-the-options-in-the-reader-page-aa-tab/1294"
            >
              Learn about all the above options
            </a>
          </small>
        </p>
      </div>
    );
  }
}

const UserFieldValueSelector: Preact.FunctionalComponent<{
  fieldName: keyof User;
  possibleValues: {
    value: string;
    displayName: string | Preact.JSX.Element;
    title?: string;
  }[];
  style?: "fitContent" | "column";
}> = ({ fieldName, possibleValues, style }) => {
  return (
    <p class={cn({ column: style === "column" })}>
      {possibleValues.map(({ value, displayName, title }) => (
        <span
          class={cn({
            clickable: true,
            selected: models.user.get(fieldName) === value,
          })}
          onClick={() =>
            models.user.save({ [fieldName]: value }, { patch: true })
          }
          style={style === "fitContent" ? "width: fit-content" : ""}
          title={title}
        >
          {displayName}
        </span>
      ))}
    </p>
  );
};

export default FormattingOptions;
